import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import useStyles from "./MatrixConfigurationDialogStyles";
import CustomDialog from "../../../../../../../components/Dialog/CustomDialog";
import CustomButton from "../../../../../../../components/Button/CustomButton";
import CustomDropdownMultiple from "../../../../../../../components/Input/CustomDropdownMultiple";
import MatrixConfigurationApi from "../../../../../../../api/services/MatrixConfigurationApi";
import useEnv from "../../../../../../../hooks/env";
import { MatrixConfigurationRequest } from "../../../../../../../api/models/test-series/TestSeriesMatrixConfiguration";
import TestSeriesDTO from "../../../../../../../api/models/TestSeriesDTO";
import ErrorSnackbarContext from "../../../../../../../contexts/ErrorSnackbarContext";
import { MatrixConfigurationFormFields } from "../../interfaces/MatrixConfigurationFormFields";
import InfoBox from "../../../../../../../components/InfoBox";
import CustomLoading from "../../../../../../../components/Alert/CustomLoading";
import { tesaDarkRedError } from "../../../../../../../styles/colors";
import ConfirmationDialogContext from "../../../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import matrixConfigurationOptionsMap from "../../utils/MatrixConfigurationOptionsMap";
import matrixConfigurationOptionsReverseMap from "../../utils/MatrixConfigurationOptionsReverseMap";

interface MappedMatrixConfiguration {
  isDefault: boolean;
  columns: string[];
  rows: string[];
  options: string[];
}

export interface MatrixConfigurationDialogProps {
  testSeries: TestSeriesDTO;
  open: boolean;
  handleDialogOpen: (v: boolean) => void;
}

const MatrixConfigurationDialog: React.FC<MatrixConfigurationDialogProps> = (props) => {
  const { api } = useEnv();
  const matrixConfigApi = new MatrixConfigurationApi(api);

  const formRef = useRef<FormikProps<MatrixConfigurationFormFields>>(null);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const { open, handleDialogOpen, testSeries } = props;

  const [loading, setLoading] = useState<boolean>(true);

  const [matrixConfiguration, setMatrixConfiguration] = useState<MappedMatrixConfiguration>({
    isDefault: false,
    columns: [],
    rows: [],
    options: [],
  });

  const classes = useStyles();

  const matrixConfigView = (data: MappedMatrixConfiguration) => {
    const columns = data.columns.map((column) => matrixConfigurationOptionsMap.get(column)!);
    const rows = data.rows.map((row) => matrixConfigurationOptionsMap.get(row)!);
    const options = data.options.map((option) => matrixConfigurationOptionsMap.get(option)!);
    setMatrixConfiguration({ columns, rows, options, isDefault: data.isDefault });
  };

  const reverseMatrixConfig = (values: MatrixConfigurationFormFields): MatrixConfigurationFormFields => {
    return {
      columns: values.columns.map((column) => matrixConfigurationOptionsReverseMap.get(column)!),
      rows: values.rows.map((row) => matrixConfigurationOptionsReverseMap.get(row)!),
    };
  };

  const showMatrixConfigurationConfirmationDialog = () => {
    if (formRef && formRef.current && formRef.current.dirty) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the matrix configuration will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      }).then((choice) => {
        if (choice === "DEFAULT") {
          formRef?.current?.handleSubmit();
        } else {
          formRef?.current?.handleReset();
        }
        handleDialogOpen(false);
      });
    } else {
      handleDialogOpen(false);
    }
  };

  const handleOnSubmit = (values: MatrixConfigurationFormFields) => {
    const { columns, rows } = reverseMatrixConfig(values);
    matrixConfigApi
      .updateMatrixConfiguration(testSeries.id, testSeries.version, { columns, rows } as MatrixConfigurationRequest)
      .then(({ data }) => {
        matrixConfigView(data);
        handleDialogOpen(false);
      })
      .then(() => showSnackbar("Matrix configuration updated", "success"))
      .catch(handleHttpError);
  };

  const handleOnDefault = () => {
    matrixConfigApi
      .getDefaultMatrixConfiguration(testSeries.id, testSeries.version)
      .then(({ data }) => {
        matrixConfigView(data);
      })
      .then(() => showSnackbar("Default matrix configuration loaded", "success"))
      .catch(handleHttpError);
  };

  const availableOptions = (selectedRowOptions: string[], selectedColumnOptions: string[], options: string[]) => {
    return {
      availableRowOptions: options.filter((option) => !selectedColumnOptions.includes(option)),
      availableColumnOptions: options.filter((option) => !selectedRowOptions.includes(option)),
    };
  };

  useEffect(() => {
    matrixConfigApi
      .getMatrixConfiguration(testSeries.id, testSeries.version)
      .then(({ data }) => {
        matrixConfigView(data);
      })
      .catch(handleHttpError)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <CustomLoading />;
  }

  const getInfoText = (option: string) => {
    return `Select the ${option} to be displayed in the test results matrix. 
    The order in which the ${option} are shown in the input field illustrates the order in the test result matrix.`;
  };

  const validationSchema = Yup.object().shape({
    rows: Yup.array().of(Yup.string().required()).min(1, "At least one parameter must be selected."),
    columns: Yup.array().of(Yup.string().required()).min(1, "At least one parameter must be selected."),
    internalMatrixConfigurationValidation: Yup.boolean().test(
      "matrixConfigurationValidation",
      "Error: All matrix configuration parameters must be distributed among the row and column fields.",
      // eslint-disable-next-line func-names
      function () {
        // eslint-disable-next-line react/no-this-in-sfc
        const {
          parent: { rows, columns },
        } = this;
        const matrixConfigurationArrayLength = matrixConfiguration.options.length;
        const rowsColumnCombinedLength = [...columns, ...rows].length;
        return matrixConfigurationArrayLength === rowsColumnCombinedLength;
      },
    ),
  });

  return (
    <CustomDialog fullWidth maxWidth="sm" open={open} onClose={showMatrixConfigurationConfirmationDialog}>
      <div className={classes.header}>
        <h2>Matrix Configuration</h2>
        <p>Configure row and column parameters for the test result matrix</p>
      </div>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={{
          rows: matrixConfiguration.rows,
          columns: matrixConfiguration.columns,
        }}
        onSubmit={handleOnSubmit}
        validationSchema={validationSchema}
      >
        {(formikProps: FormikProps<MatrixConfigurationFormFields>): ReactNode => {
          const { dirty, errors, touched, handleChange, isSubmitting, values } = formikProps;
          const { availableRowOptions, availableColumnOptions } = availableOptions(
            values.rows,
            values.columns,
            matrixConfiguration.options,
          );

          return (
            <Form>
              <Grid container direction="column" spacing={3}>
                <Grid item className={classes.dropdownItems}>
                  <InfoBox text={getInfoText("rows")}>
                    <CustomDropdownMultiple
                      chips
                      name="rows"
                      labelText="Rows"
                      options={availableRowOptions}
                      value={values.rows}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      error={!!errors.rows && !!touched.rows}
                      helperText={errors.rows && touched.rows ? String(errors.rows) : ""}
                    />
                  </InfoBox>
                  <InfoBox text={getInfoText("columns")}>
                    <CustomDropdownMultiple
                      chips
                      name="columns"
                      labelText="Columns"
                      options={availableColumnOptions}
                      value={values.columns}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      error={!!errors.columns && !!touched.columns}
                      helperText={errors.columns && touched.columns ? String(errors.columns) : ""}
                    />
                  </InfoBox>
                  <Box className={classes.matrixConfigurationError}>
                    {errors && errors?.internalMatrixConfigurationValidation}
                  </Box>
                </Grid>
                <Grid container item direction="row" justify="flex-end">
                  <Box>
                    <CustomButton
                      disabled={isSubmitting}
                      text="Use Default Configuration"
                      type="reset"
                      onClick={handleOnDefault}
                    />
                  </Box>
                  <Box marginLeft={3}>
                    <CustomButton disabled={isSubmitting || !dirty} text="Revert" type="reset" />
                  </Box>
                  <Box marginLeft={3}>
                    <CustomButton disabled={isSubmitting} primary text="Save" type="submit" />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default MatrixConfigurationDialog;
